// ============================================
// UI Store - Zustand
// ============================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface SidebarState {
  isCollapsed: boolean;
  isMobileOpen: boolean;
}

interface ModalState {
  isOpen: boolean;
  modalId: string | null;
  modalData: unknown;
}

interface UIState {
  // Sidebar
  sidebar: SidebarState;
  toggleSidebar: () => void;
  collapseSidebar: () => void;
  expandSidebar: () => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;

  // Theme
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;

  // Modal
  modal: ModalState;
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;

  // Breadcrumb
  breadcrumbs: Array<{ label: string; path?: string }>;
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; path?: string }>) => void;

  // Page title
  pageTitle: string;
  setPageTitle: (title: string) => void;

  // Loading overlay
  isGlobalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
  globalLoadingMessage: string | null;
  setGlobalLoadingMessage: (message: string | null) => void;
}

const initialSidebar: SidebarState = {
  isCollapsed: false,
  isMobileOpen: false,
};

const initialModal: ModalState = {
  isOpen: false,
  modalId: null,
  modalData: null,
};

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Sidebar
      sidebar: initialSidebar,
      toggleSidebar: () =>
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isCollapsed: !state.sidebar.isCollapsed,
          },
        })),
      collapseSidebar: () =>
        set((state) => ({
          sidebar: { ...state.sidebar, isCollapsed: true },
        })),
      expandSidebar: () =>
        set((state) => ({
          sidebar: { ...state.sidebar, isCollapsed: false },
        })),
      toggleMobileSidebar: () =>
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isMobileOpen: !state.sidebar.isMobileOpen,
          },
        })),
      closeMobileSidebar: () =>
        set((state) => ({
          sidebar: { ...state.sidebar, isMobileOpen: false },
        })),

      // Theme
      theme: 'system',
      setTheme: (theme) => set({ theme }),

      // Modal
      modal: initialModal,
      openModal: (modalId, data = null) =>
        set({
          modal: {
            isOpen: true,
            modalId,
            modalData: data,
          },
        }),
      closeModal: () => set({ modal: initialModal }),

      // Breadcrumb
      breadcrumbs: [],
      setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),

      // Page title
      pageTitle: '',
      setPageTitle: (pageTitle) => set({ pageTitle }),

      // Global loading
      isGlobalLoading: false,
      setGlobalLoading: (isGlobalLoading) => set({ isGlobalLoading }),
      globalLoadingMessage: null,
      setGlobalLoadingMessage: (globalLoadingMessage) => set({ globalLoadingMessage }),
    }),
    {
      name: 'conai-admin-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebar: { isCollapsed: state.sidebar.isCollapsed },
        theme: state.theme,
      }),
    }
  )
);

// ============================================
// Selectors
// ============================================

export const selectSidebar = (state: UIState) => state.sidebar;
export const selectIsSidebarCollapsed = (state: UIState) => state.sidebar.isCollapsed;
export const selectIsMobileSidebarOpen = (state: UIState) => state.sidebar.isMobileOpen;
export const selectTheme = (state: UIState) => state.theme;
export const selectModal = (state: UIState) => state.modal;
export const selectBreadcrumbs = (state: UIState) => state.breadcrumbs;
export const selectPageTitle = (state: UIState) => state.pageTitle;
export const selectIsGlobalLoading = (state: UIState) => state.isGlobalLoading;
